import * as dashboardApi from '@/api/dashboard'
import * as socialApi from '@/api/social'
import * as localeApi from '@/api/locale'
import * as appApi from '@/api/app'

const LOCALE_STORAGE_KEY = 'sociallabs_locale_prefs'

function loadStoredPrefs() {
  try {
    const raw = localStorage.getItem(LOCALE_STORAGE_KEY)
    if (raw) return JSON.parse(raw)
  } catch {
    // ignore invalid storage
  }
  return null
}

function saveStoredPrefs(prefs) {
  localStorage.setItem(LOCALE_STORAGE_KEY, JSON.stringify(prefs))
}

const state = {
  dashboard: null,
  accounts: [],
  accountsLoading: false,
  loading: false,
  locale: 'en',
  currency: 'USD',
  countryCode: '',
  localeReady: false,
  localeUserOverride: false,
  featureSettings: {
    hide_scheduler: false,
    hide_ai: false,
    loaded: false,
  },
}

const getters = {
  dashboard: (state) => state.dashboard,
  accounts: (state) => state.accounts,
  accountsLoading: (state) => state.accountsLoading,
  loading: (state) => state.loading,
  locale: (state) => state.locale,
  currency: (state) => state.currency,
  countryCode: (state) => state.countryCode,
  localeReady: (state) => state.localeReady,
  isIndonesia: (state) => state.countryCode === 'ID' || state.locale === 'id',
  featureSettings: (state) => state.featureSettings,
  hideScheduler: (state, getters, rootState, rootGetters) => (
    rootGetters['auth/role'] !== 'admin' && state.featureSettings.hide_scheduler
  ),
  hideAI: (state, getters, rootState, rootGetters) => (
    rootGetters['auth/role'] !== 'admin' && state.featureSettings.hide_ai
  ),
}

const actions = {
  async fetchDashboard({ commit }) {
    commit('SET_LOADING', true)
    try {
      const { data } = await dashboardApi.getDashboard()
      commit('SET_DASHBOARD', data.data)
      return data.data
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchAccounts({ commit, rootGetters }) {
    if (rootGetters['app/hideScheduler']) {
      commit('SET_ACCOUNTS', [])
      return []
    }
    commit('SET_ACCOUNTS_LOADING', true)
    try {
      const { data } = await socialApi.listAccounts()
      commit('SET_ACCOUNTS', data.data.accounts || [])
      return data.data.accounts
    } catch (e) {
      if (e.response?.status === 403) {
        commit('SET_ACCOUNTS', [])
        return []
      }
      throw e
    } finally {
      commit('SET_ACCOUNTS_LOADING', false)
    }
  },

  async fetchAccountProfile(_, id) {
    const { data } = await socialApi.getAccount(id)
    return data.data
  },

  async connectPlatform(_, platform) {
    const { data } = await socialApi.connectPlatform(platform)
    if (data.data.url) {
      window.location.href = data.data.url
    }
    return data
  },

  async connectInstagramToken({ dispatch }, accessToken) {
    const { data } = await socialApi.connectInstagramToken(accessToken)
    await dispatch('fetchAccounts')
    return data.data.account
  },

  async disconnectAccount({ dispatch }, id) {
    await socialApi.disconnectAccount(id)
    return dispatch('fetchAccounts')
  },

  async fetchFeatureSettings({ commit }) {
    try {
      const { data } = await appApi.getFeatureSettings()
      const settings = data.data || data
      commit('SET_FEATURE_SETTINGS', {
        hide_scheduler: !!settings.hide_scheduler,
        hide_ai: !!settings.hide_ai,
        loaded: true,
      })
    } catch {
      commit('SET_FEATURE_SETTINGS', { hide_scheduler: false, hide_ai: false, loaded: true })
    }
  },

  async initLocale({ commit, state }, i18n) {
    const stored = loadStoredPrefs()
    if (stored?.localeUserOverride) {
      commit('SET_LOCALE_PREFS', stored)
      i18n.locale = stored.locale
      commit('SET_LOCALE_READY', true)
      return
    }

    try {
      const { data } = await localeApi.detectLocale()
      const prefs = data.data || {}
      commit('SET_LOCALE_PREFS', {
        locale: prefs.locale || 'en',
        currency: prefs.currency || 'USD',
        countryCode: prefs.country_code || '',
        localeUserOverride: false,
      })
    } catch {
      commit('SET_LOCALE_PREFS', {
        locale: 'en',
        currency: 'USD',
        countryCode: '',
        localeUserOverride: false,
      })
    }

    i18n.locale = state.locale
    commit('SET_LOCALE_READY', true)
  },

  setLocale({ commit }, { locale, i18n }) {
    const currency = locale === 'id' ? 'IDR' : 'USD'
    const prefs = {
      locale,
      currency,
      countryCode: locale === 'id' ? 'ID' : 'US',
      localeUserOverride: true,
    }
    commit('SET_LOCALE_PREFS', prefs)
    saveStoredPrefs(prefs)
    if (i18n) i18n.locale = locale
  },
}

const mutations = {
  SET_DASHBOARD(state, dashboard) {
    state.dashboard = dashboard
  },
  SET_ACCOUNTS(state, accounts) {
    state.accounts = accounts
  },
  PATCH_ACCOUNT(state, account) {
    const idx = state.accounts.findIndex((item) => item.id === account.id)
    if (idx === -1) {
      state.accounts = [account, ...state.accounts]
      return
    }
    state.accounts.splice(idx, 1, { ...state.accounts[idx], ...account })
  },
  SET_ACCOUNTS_LOADING(state, loading) {
    state.accountsLoading = loading
  },
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_LOCALE_PREFS(state, prefs) {
    state.locale = prefs.locale || 'en'
    state.currency = prefs.currency || 'USD'
    state.countryCode = prefs.countryCode || ''
    state.localeUserOverride = !!prefs.localeUserOverride
  },
  SET_LOCALE_READY(state, ready) {
    state.localeReady = ready
  },
  SET_FEATURE_SETTINGS(state, settings) {
    state.featureSettings = {
      hide_scheduler: !!settings.hide_scheduler,
      hide_ai: !!settings.hide_ai,
      loaded: !!settings.loaded,
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}

